import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, MessageSquareWarning, StickyNote, Link2, Clock, ShieldCheck, FileHeart, BellRing, Siren, Download, Users, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MarketingPage } from "@/components/Marketing";
import { useSeo } from "@/lib/useSeo";
import { FAQ } from "@/lib/content";

const DEMO = [
  { pet: "Max", img: "/img/dog.jpg", title: "Insulin", time: "8:00 AM", done: "Sam · 8:04 AM" },
  { pet: "Luna", img: "/img/cat.jpg", title: "Breakfast · ½ cup", time: "8:00 AM", done: "Jess · 7:52 AM" },
  { pet: "Pepper", img: "/img/rabbit.jpg", title: "Fresh hay & water", time: "9:00 AM" },
  { pet: "Max", img: "/img/dog.jpg", title: "Evening walk", time: "6:00 PM" },
];

function PhoneMock() {
  return (
    <div className="relative mx-auto w-[300px] sm:w-[330px] rounded-[2.6rem] bg-[#1E293B] p-3 shadow-[0_30px_60px_-20px_rgba(30,41,59,.45)] rotate-[2deg]" data-testid="hero-phone-mock">
      <div className="rounded-[2rem] bg-[#FAF7F2] p-4 h-[560px] overflow-hidden">
        <p className="text-xs font-semibold text-slate-500">Tuesday</p>
        <p className="font-heading text-2xl font-bold mb-1">Today</p>
        <div className="h-2 rounded-full bg-[#EFECE6] mb-4"><div className="h-2 w-1/2 rounded-full bg-[#10B981]" /></div>
        <div className="space-y-2.5">
          {DEMO.map((d, i) => (
            <div key={i} className={`rounded-2xl border p-3 flex items-center gap-3 ${d.done ? "bg-[#F3FBF7] border-[#BBF0D9]" : "bg-white border-[#EFECE6]"}`}>
              <img src={d.img} alt={d.pet} className="h-11 w-11 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-[#D97706]">{d.pet} · {d.time}</p>
                <p className="font-heading font-semibold text-sm truncate">{d.title}</p>
                {d.done && <p className="text-[11px] text-[#047857] font-semibold">Done by {d.done}</p>}
              </div>
              <span className={`h-10 w-10 rounded-full grid place-items-center ${d.done ? "bg-[#10B981] text-white" : "border-2 border-dashed border-[#F59E0B] text-[#F59E0B]"}`}><Check className="h-5 w-5" strokeWidth={3} /></span>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-2xl bg-[#1E293B] text-white p-3 text-xs">
          <p className="font-semibold flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" />Sitter link active</p>
          <p className="text-slate-300">Priya can log tasks until Sunday 6 PM</p>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden grain">
      <div className="max-w-6xl mx-auto px-5 pt-14 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <div className="rise">
          <span className="pl-chip bg-[#ECFDF5] text-[#047857] mb-5"><Sparkles className="h-3.5 w-3.5" />One price. Every pet.</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] text-[#1E293B]">
            “Did anyone<br />feed the <span className="text-[#F59E0B] italic">cat?</span>”
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-lg">One shared log for every pet and everyone who cares for them. Tap once when it's done, so nobody double-feeds, double-doses, or texts “did Bella get her pill?”</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" className="pl-btn-primary text-base !px-7 !min-h-[56px]" data-testid="hero-start-trial-btn">Start free trial</Link>
            <a href="#sitter" className="pl-btn-ghost !min-h-[56px]" data-testid="hero-see-sitter-btn">See sitter links</a>
          </div>
          <p className="mt-4 text-sm text-slate-500">14 days free · No credit card · Unlimited pets & caregivers</p>
        </div>
        <div className="rise rise-2 relative">
          <div className="absolute -top-6 -left-4 h-40 w-40 rounded-full bg-[#FDE68A] blur-3xl opacity-70" />
          <div className="absolute bottom-0 right-0 h-48 w-48 rounded-full bg-[#A7F3D0] blur-3xl opacity-60" />
          <PhoneMock />
        </div>
      </div>
    </section>
  );
}

function Problem() {
  const items = [
    { icon: MessageSquareWarning, t: "Group texts", d: "“Did you give Max his insulin?” “I thought YOU did.”" },
    { icon: StickyNote, t: "Fridge sticky notes", d: "Great until the sitter is at the dog park and can't see them." },
    { icon: Clock, t: "Memory", d: "Was the flea dose the 1st or the 15th? Nobody remembers." },
  ];
  return (
    <section className="max-w-6xl mx-auto px-5 py-16">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold max-w-2xl">Multi-pet homes run on guesswork. PackLog replaces it with one live checklist.</h2>
      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        {items.map((i) => (
          <div key={i.t} className="pl-card p-6"><i.icon className="h-7 w-7 text-[#E07A5F] mb-3" />
            <p className="font-heading font-semibold text-xl">{i.t}</p><p className="text-slate-600 mt-1">{i.d}</p></div>
        ))}
      </div>
    </section>
  );
}

function SitterSection() {
  return (
    <section id="sitter" className="bg-[#1E293B] text-white py-20">
      <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="pl-chip bg-white/10 text-[#FDE68A] mb-4"><Link2 className="h-3.5 w-3.5" />Sitter mode</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Going away for the weekend? Send one link.</h2>
          <p className="mt-4 text-slate-300 text-lg">Your sitter or dog walker opens the link, sees today's checklist and every care instruction, and logs tasks with one tap. No app, no account. The link expires on its own when you're back.</p>
          <ul className="mt-6 space-y-3 text-slate-200">
            {["Live feed of everything your sitter logs", "Auto-built care guide: food, meds, quirks, vet and emergency contact", "Set start and end times, revoke anytime"].map((x) => (
              <li key={x} className="flex gap-2"><Check className="h-5 w-5 text-[#10B981] shrink-0" />{x}</li>))}
          </ul>
        </div>
        <div className="grid gap-4">
          <div className="rounded-3xl bg-white text-[#1E293B] p-5 rotate-[-1.5deg]">
            <p className="text-xs font-semibold text-slate-500">priya's view · expires Sun 6:00 PM</p>
            <div className="mt-3 flex items-center gap-3 rounded-2xl bg-[#F3FBF7] border border-[#BBF0D9] p-3">
              <img src="/img/beagle.jpg" alt="Beagle" className="h-12 w-12 rounded-full object-cover" />
              <div className="flex-1"><p className="font-heading font-semibold">Dinner · 1 cup</p><p className="text-xs text-[#047857] font-semibold">Done by Priya · 6:02 PM</p></div>
              <span className="h-10 w-10 rounded-full bg-[#10B981] text-white grid place-items-center"><Check className="h-5 w-5" /></span>
            </div>
          </div>
          <div className="rounded-3xl bg-[#FFF7E6] text-[#1E293B] p-5 rotate-[1deg] ml-6">
            <p className="text-xs font-semibold text-[#B45309]">Your live feed</p>
            <p className="mt-2 font-semibold">Priya (sitter) logged Dinner for Biscuit</p>
            <p className="text-sm text-slate-600">2 min ago</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const f = [
    { icon: Users, t: "Shared household", d: "Partner, kids, roommates: everyone sees the same live list." },
    { icon: FileHeart, t: "Vet records, read for you", d: "Snap a photo of vet paperwork. AI pulls vaccine names and due dates; you confirm before anything is saved." },
    { icon: BellRing, t: "Reminders that nag (nicely)", d: "Upcoming vaccines and preventives, plus an alert if a medication isn't logged within an hour." },
    { icon: Siren, t: "Emergency card", d: "Meds, allergies, microchip and vet phone, one tap away." },
    { icon: Download, t: "Your data, always", d: "Export everything as PDF or CSV anytime. Daily automatic backups." },
    { icon: ShieldCheck, t: "Never sold. Ever.", d: "We don't sell or share your data. We don't give medical advice either." },
  ];
  return (
    <section className="max-w-6xl mx-auto px-5 py-20">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold">Everything for the whole pack</h2>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {f.map((x, i) => (
          <div key={x.t} className={`pl-card p-6 ${i === 1 ? "lg:row-span-2 bg-[#FFF7E6]" : ""}`}>
            <x.icon className="h-7 w-7 text-[#D97706] mb-3" />
            <p className="font-heading font-semibold text-xl">{x.t}</p><p className="text-slate-600 mt-1">{x.d}</p>
            {i === 1 && <img src="/img/family.jpg" alt="Family with pets" className="mt-6 rounded-2xl h-56 w-full object-cover" />}
          </div>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  const [yearly, setYearly] = useState(true);
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-5 py-20">
      <div className="pl-card p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden">
        <div className="absolute -right-10 -top-10 h-52 w-52 rounded-full bg-[#FDE68A] opacity-60" />
        <div className="relative">
          <span className="pl-chip bg-[#ECFDF5] text-[#047857]">No per-pet fees</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold mt-4">One price.<br />Every pet.</h2>
          <p className="mt-4 text-slate-600 text-lg">Two dogs, three cats and a rabbit? Same price. Unlimited pets, unlimited caregivers, unlimited sitter links.</p>
          <div className="flex -space-x-3 mt-6">{["dog", "cat", "rabbit", "beagle", "tabby"].map((p) => <img key={p} src={`/img/${p}.jpg`} alt={p} className="h-14 w-14 rounded-full ring-4 ring-white object-cover" />)}</div>
        </div>
        <div className="relative rounded-3xl bg-[#1E293B] text-white p-7">
          <div className="inline-flex rounded-full bg-white/10 p-1 mb-6" data-testid="pricing-toggle">
            <button data-testid="pricing-yearly-btn" onClick={() => setYearly(true)} className={`px-4 py-2 rounded-full text-sm font-semibold ${yearly ? "bg-[#F59E0B] text-[#1E293B]" : ""}`}>Yearly</button>
            <button data-testid="pricing-monthly-btn" onClick={() => setYearly(false)} className={`px-4 py-2 rounded-full text-sm font-semibold ${!yearly ? "bg-[#F59E0B] text-[#1E293B]" : ""}`}>Monthly</button>
          </div>
          <p className="font-heading text-6xl font-extrabold" data-testid="pricing-amount">{yearly ? "$29" : "$3.99"}<span className="text-xl font-semibold text-slate-400">/{yearly ? "year" : "month"}</span></p>
          <p className="text-slate-300 mt-1">per household {yearly && "· about $2.42/month"}</p>
          <ul className="mt-6 space-y-2.5 text-slate-200 text-sm">
            {["Unlimited pets & caregivers", "Unlimited time-limited sitter links", "AI vet-record reading", "Email & in-app reminders", "PDF / CSV export anytime"].map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-[#10B981]" />{x}</li>)}
          </ul>
          <Link to="/signup" className="pl-btn-primary w-full mt-7" data-testid="pricing-start-trial-btn">Start 14-day free trial</Link>
          <p className="text-xs text-slate-400 mt-3 text-center">No credit card to start. Cancel anytime.</p>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="max-w-3xl mx-auto px-5 py-10">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">Questions</h2>
      <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
        {FAQ.map((f, i) => (
          <AccordionItem key={i} value={`f${i}`} className="pl-card px-5 border">
            <AccordionTrigger data-testid={`faq-item-${i}`} className="font-heading text-left text-lg font-semibold">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-600 text-base">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export default function Landing() {
  useSeo(null, "PackLog: one shared care log for every pet and everyone who cares for them. One price, unlimited pets.");
  React.useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("pl_ref", ref);
  }, []);
  return (
    <MarketingPage>
      <Hero />
      <Problem />
      <SitterSection />
      <Features />
      <Pricing />
      <Faq />
      <section className="max-w-6xl mx-auto px-5 pt-16 text-center">
        <h2 className="font-heading text-4xl sm:text-5xl font-extrabold">Never ask “did anyone feed the cat?” again.</h2>
        <Link to="/signup" className="pl-btn-primary mt-8 !min-h-[56px] !px-8 text-base" data-testid="footer-cta-start-trial-btn">Start free trial</Link>
      </section>
    </MarketingPage>
  );
}
